import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import axios from 'src/utils/axios';
import ModalDialogMUI6 from 'src/components/ModalDialogMUI6';
import Spinner from 'src/components/Spinner/Spinner';
import TextFieldFormik from 'src/components/TextFieldFormik';
import { saveFormModifyGuests } from 'src/actions/partyDetailsActions';
import { addNotification } from 'src/actions/notificationActions';
import { CONTACTS, NOTIFICATION_STATUS, STYLE_OPTIONS } from 'src/constants';
import helpers from 'src/helpers';

const {
  BOOKED_EVENT_SCREEN_OPTIONS,
  BOOKED_EVENT_DB_OPTIONS,
  JOIN_AS_CONSULTANT_SCREEN_OPTIONS,
  JOIN_AS_CONSULTANT_DB_OPTIONS,
} = CONTACTS;

const GET_CONTACT_EVENTS_ERROR = '@booking-joining/get-contact-events-error';

const EVENT_GROUPS = {
  OPEN: 'Open Events',
  CLOSED: 'Closed Events',
};

// Entry types are config-driven so future types (e.g. event-level smart
// fields) can be added as new entries without restructuring the modal.
// fieldName is the partyGuest column written by the Modify Guests endpoint.
const ENTRY_TYPES = [
  {
    key: 'bookedEvent',
    label: 'Booked an Event',
    fieldName: 'action3',
    screenOptions: BOOKED_EVENT_SCREEN_OPTIONS,
    dbOptions: BOOKED_EVENT_DB_OPTIONS,
  },
  {
    key: 'joiningConsultant',
    label: 'Joining as Consultant',
    fieldName: 'action4',
    screenOptions: JOIN_AS_CONSULTANT_SCREEN_OPTIONS,
    dbOptions: JOIN_AS_CONSULTANT_DB_OPTIONS,
  },
];

/*
 * BookingJoiningModal
 *
 * Shared by the Connections page and Contact detail page kebab menus.
 * Lets the user set a contact's Booked an Event (action3) or Joining as
 * Consultant (action4) value on one of their events without opening the
 * event's Guests tab. Saves through the existing Modify Guests endpoint.
 */
function BookingJoiningModal({
  isVisible = false,
  onClose = () => { },
  contact = {},
}) {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [partyGuests, setPartyGuests] = useState(null);
  const [entryTypeKey, setEntryTypeKey] = useState(ENTRY_TYPES[0].key);
  const [selectedPartyGuestId, setSelectedPartyGuestId] = useState('');
  const [newValue, setNewValue] = useState('');

  const {
    id: contactId,
    firstName,
    lastName,
  } = contact || {};

  const contactName = helpers.transform.arrayToStringWithoutEmpty([firstName, lastName], ' ');

  const entryType = ENTRY_TYPES.find((type) => type.key === entryTypeKey) || ENTRY_TYPES[0];

  const partyStartTime = (partyGuest) => {
    const start = partyGuest?.party?.partyStartDateTime;
    return start ? moment(start).valueOf() : 0;
  };

  const sortByStartDesc = (a, b) => partyStartTime(b) - partyStartTime(a);

  // Options for the event Autocomplete: open events first (newest first),
  // then closed events, grouped under Open Events / Closed Events headers.
  const eventOptions = useMemo(() => {
    const rows = helpers.transform.toArray(partyGuests)
      .filter((partyGuest) => partyGuest?.party);
    const toOption = (partyGuest) => {
      const {
        partyName,
        hostFirstName,
        hostLastName,
        hostContact,
        partyStartDateTime,
        partyEndDateTime,
        closed,
      } = partyGuest.party || {};
      const startDate = partyStartDateTime ? moment(partyStartDateTime).format('MM/DD/YY') : '';
      const endDate = partyEndDateTime ? moment(partyEndDateTime).format('MM/DD/YY') : '';
      // The host lives on the hostContact association; the party's own
      // hostFirstName/hostLastName columns are only a legacy fallback
      // (same derivation as convertPartyToPartyViewModel on the server).
      const hostName = helpers.transform.arrayToStringWithoutEmpty(
        [hostContact?.firstName, hostContact?.lastName],
        ' '
      ) || helpers.transform.arrayToStringWithoutEmpty([hostFirstName, hostLastName], ' ');
      return {
        value: String(partyGuest.id),
        label: partyName || `Event ${startDate}`.trim(),
        partyName,
        hostName,
        startDate,
        endDate,
        group: closed ? EVENT_GROUPS.CLOSED : EVENT_GROUPS.OPEN,
      };
    };
    const open = rows.filter((partyGuest) => !partyGuest.party.closed).sort(sortByStartDesc);
    const closed = rows.filter((partyGuest) => partyGuest.party.closed).sort(sortByStartDesc);
    return [...open.map(toOption), ...closed.map(toOption)];
  }, [partyGuests]);

  const hasEvents = eventOptions.length > 0;

  // Default event: the most recent open event whose start date is not in
  // the future; otherwise the most recent open event; otherwise the most
  // recent closed one.
  const getDefaultPartyGuestId = (rows) => {
    const now = moment().valueOf();
    const open = rows.filter((partyGuest) => !partyGuest.party.closed).sort(sortByStartDesc);
    const closed = rows.filter((partyGuest) => partyGuest.party.closed).sort(sortByStartDesc);
    const openStarted = open.find((partyGuest) => partyStartTime(partyGuest) <= now);
    const defaultGuest = openStarted || open[0] || closed[0];
    return defaultGuest ? String(defaultGuest.id) : '';
  };

  useEffect(() => {
    if (!isVisible || !contactId) {
      return;
    }
    let ignore = false;
    const fetchContactEvents = async () => {
      setLoading(true);
      setPartyGuests(null);
      setNewValue('');
      setEntryTypeKey(ENTRY_TYPES[0].key);
      try {
        const params = {
          screen: 'contacts',
          sort: {
            table2: null,
            column1: 'joinedPartyDateTime',
            column2: null,
            direction1: 'desc',
            direction2: null,
          },
          page: 1,
          pageSize: 500,
        };
        const response = await axios.post(`/api/parties/${contactId}/guests`, { params });
        if (ignore) {
          return;
        }
        const rows = helpers.transform.toArray(response?.data?.partyGuests)
          .filter((partyGuest) => partyGuest?.party);
        setPartyGuests(rows);
        setSelectedPartyGuestId(getDefaultPartyGuestId(rows));
      } catch (e) {
        if (ignore) {
          return;
        }
        const { error, message } = e?.response?.data || {};
        dispatch(addNotification(GET_CONTACT_EVENTS_ERROR, helpers.transform.toString(message || error || 'Server error'), NOTIFICATION_STATUS.ERROR));
        setPartyGuests([]);
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };
    fetchContactEvents();
    // eslint-disable-next-line consistent-return
    return () => {
      ignore = true;
    };
  }, [isVisible, contactId, dispatch]);

  const selectedPartyGuest = helpers.transform.toArray(partyGuests)
    .find((partyGuest) => String(partyGuest.id) === String(selectedPartyGuestId));

  const selectedEventOption = eventOptions
    .find((option) => option.value === String(selectedPartyGuestId)) || null;

  // Map the stored DB value to its screen label. A null/empty column is the
  // neutral "Has Not" state ('N').
  const currentValueLabel = useMemo(() => {
    if (!selectedPartyGuest) {
      return '';
    }
    const dbValue = selectedPartyGuest[entryType.fieldName] || 'N';
    const optionKey = Object.keys(entryType.dbOptions)
      .find((key) => entryType.dbOptions[key] === dbValue);
    return optionKey ? entryType.screenOptions[optionKey] : dbValue;
  }, [selectedPartyGuest, entryType]);

  const handleChangeEntryType = (event) => {
    setEntryTypeKey(event.target.value);
    setNewValue('');
  };

  const handleChangeEvent = (event, value) => {
    setSelectedPartyGuestId(value?.value || '');
    setNewValue('');
  };

  const handleChangeNewValue = (event) => {
    setNewValue(event.target.value);
  };

  const handleClose = () => {
    if (saving) {
      return;
    }
    onClose();
  };

  const handleSave = () => {
    if (!selectedPartyGuest || !newValue) {
      return;
    }
    const optionKey = Object.keys(entryType.screenOptions)
      .find((key) => entryType.screenOptions[key] === newValue);
    const dbValue = optionKey ? entryType.dbOptions[optionKey] : null;
    if (!dbValue) {
      return;
    }
    const guestChanges = {
      guestIds: [selectedPartyGuest.id],
      [entryType.fieldName]: dbValue,
    };
    const doWhenTry = () => {
      setPartyGuests((rows) => helpers.transform.toArray(rows).map((partyGuest) => (
        partyGuest.id === selectedPartyGuest.id
          ? { ...partyGuest, [entryType.fieldName]: dbValue }
          : partyGuest
      )));
      setNewValue('');
      setSaving(false);
      onClose();
    };
    const doWhenCatch = () => {
      setSaving(false);
    };
    setSaving(true);
    dispatch(saveFormModifyGuests(guestChanges, doWhenTry, doWhenCatch));
  };

  // Typing in the event field matches against the event name AND the
  // host's name (same pattern as the Customize Orders event picker).
  const filterOptionsEvents = createFilterOptions({
    stringify: ({ label, hostName }) => `${label} ${hostName}`,
  });

  const renderOptionAutocompleteEvents = (props, option) => (
    <li
      {...props}
      key={option.value}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
      }}
    >
      <b>{option.label}</b>
      {option.hostName && (
        <Typography
          variant="body2"
          color="textSecondary"
        >
          Host:
          {' '}
          {option.hostName}
        </Typography>
      )}
      {option.startDate && (
        <Typography
          variant="body2"
          color="textSecondary"
        >
          Dates:
          {' '}
          {option.endDate ? `${option.startDate} - ${option.endDate}` : option.startDate}
        </Typography>
      )}
    </li>
  );

  const renderForm = () => (
    <>
      <Box
        mt={1}
        mb={3}
      >
        <TextFieldFormik
          name="bookingJoiningType"
          label="Type"
          value={entryTypeKey}
          handleChange={handleChangeEntryType}
          disabled={saving}
          SelectProps={{ native: true }}
          select
          fullWidth
        >
          {ENTRY_TYPES.map((type) => (
            <option
              key={type.key}
              value={type.key}
            >
              {type.label}
            </option>
          ))}
        </TextFieldFormik>
      </Box>
      <Box mb={3}>
        <Autocomplete
          multiple={false}
          disabled={saving}
          options={eventOptions}
          filterOptions={filterOptionsEvents}
          groupBy={(option) => option.group}
          getOptionLabel={(option) => option?.label || ''}
          renderOption={renderOptionAutocompleteEvents}
          isOptionEqualToValue={(option, value) => option.value === value?.value}
          value={selectedEventOption}
          onChange={handleChangeEvent}
          renderInput={(params) => (
            <TextField
              label="Event"
              {...params}
            />
          )}
        />
      </Box>
      {/*
        Panel (not a chip) on purpose: future entry types may be smart
        fields whose values can be long (even a paragraph), so the value
        needs room to wrap.
      */}
      <Box
        mb={3}
        sx={{
          backgroundColor: `${STYLE_OPTIONS.COLOR_BLUE}14`,
          borderRadius: '10px',
          padding: '10px 16px',
        }}
      >
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ display: 'block' }}
        >
          Current Value
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {currentValueLabel}
        </Typography>
      </Box>
      <Box mb={1}>
        <TextFieldFormik
          name="bookingJoiningNewValue"
          label="New Value"
          value={newValue}
          handleChange={handleChangeNewValue}
          disabled={saving}
          SelectProps={{ native: true }}
          select
          fullWidth
        >
          <option value="" />
          {Object.values(entryType.screenOptions).map((screenLabel) => {
            const isCurrentValue = screenLabel === currentValueLabel;
            return (
              <option
                key={screenLabel}
                value={screenLabel}
                disabled={isCurrentValue}
              >
                {isCurrentValue ? `${screenLabel} (already the current value)` : screenLabel}
              </option>
            );
          })}
        </TextFieldFormik>
      </Box>
    </>
  );

  const renderEmptyState = () => (
    <Box
      py={2}
      className="center"
    >
      <Typography variant="body1">
        {contactName || 'This contact'}
        {' '}
        is not a guest in any event.
      </Typography>
    </Box>
  );

  const renderContent = () => {
    if (loading || partyGuests === null) {
      return (
        <Box
          py={4}
          position="relative"
          minHeight="120px"
        >
          <Spinner
            loading
            positionType="absolute"
          />
        </Box>
      );
    }
    return hasEvents ? renderForm() : renderEmptyState();
  };

  return (
    <ModalDialogMUI6
      isVisible={isVisible}
      onClose={handleClose}
      title={`Booking / Joining for ${contactName}`.trim()}
      content={renderContent()}
      loading={loading || saving}
      {...hasEvents && !loading ? {
        onOk: handleSave,
        okText: 'Save',
        okDisabled: !selectedPartyGuest || !newValue,
        closeText: 'Cancel',
      } : {
        closeText: 'Close',
      }}
    />
  );
}

BookingJoiningModal.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
  contact: PropTypes.object,
};

export default BookingJoiningModal;
